import { AUTHOR, LEGAL, SAME_AS, SITE } from './links'
import type { Locale } from '../i18n'

export type SeoProps = {
  title: string
  description: string
  path?: string
  canonical?: string
  jsonLd?: object | object[]
}

export function seoHelmetProps({ title, description, path, canonical, jsonLd }: SeoProps) {
  const url = canonical ?? (path ? `${SITE.url}${path}` : SITE.url)
  return { title, description, url, jsonLd }
}

const ORG_DESCRIPTIONS: Record<Locale, string> = {
  fr: "Prestataire logistique 3PL spécialisé en e-commerce en Belgique, offrant stockage, préparation et expédition de commandes sous 24h partout en Europe, au Royaume-Uni et en Suisse.",
  en: "Belgian 3PL logistics provider specialising in e-commerce, offering storage, order fulfilment and next-day dispatch across Europe, the United Kingdom and Switzerland.",
  nl: "Belgische 3PL-logistiekpartner gespecialiseerd in e-commerce, met opslag, orderverwerking en verzending binnen 24u in heel Europa, het Verenigd Koninkrijk en Zwitserland.",
}

const ORG_SERVICE_TYPES: Record<Locale, string[]> = {
  fr: [
    'Logistique e-commerce',
    'Fulfillment 3PL',
    'Stockage',
    'Préparation de commandes',
    'Expédition',
    'Gestion des retours',
  ],
  en: [
    'E-commerce logistics',
    '3PL fulfilment',
    'Storage',
    'Order fulfilment',
    'Shipping',
    'Returns management',
  ],
  nl: [
    'E-commercelogistiek',
    '3PL-fulfilment',
    'Opslag',
    'Orderverwerking',
    'Verzending',
    'Retourbeheer',
  ],
}

export function orgSchema(locale: Locale) {
  return {
    '@type': ['Organization', 'LocalBusiness'],
    '@id': `${SITE.url}/#organization`,
    name: 'Yaslan',
    /**
     * The brand is "Yaslan", but the third-party profiles that corroborate it
     * — LinkedIn and Crunchbase — are both titled "Yaslan Logistics". Declaring
     * it here tells Google and any model that the two strings denote one
     * entity, instead of leaving them to guess at two companies with similar
     * names. Cheaper and more honest than forcing every profile to rename.
     */
    alternateName: LEGAL.company,
    legalName: `${LEGAL.company} ${LEGAL.legalForm}`,
    /**
     * Emitted only once VIES validates the number. Structured data is meant to
     * be machine-checked, and a `vatID` the EU's own checker rejects is worse
     * than no `vatID` at all: it hands any verifier a contradiction on all 165
     * pages, which is precisely what this site is trying to stop doing. The
     * number still appears on the legal pages, where Belgian law requires it —
     * that obligation is unaffected by what the JSON-LD claims.
     */
    ...(LEGAL.vatViesActive ? { vatID: LEGAL.vatCompact } : {}),
    description: ORG_DESCRIPTIONS[locale],
    url: SITE.url,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE.url}/brand/yaslan-black.svg`,
    },
    image: `${SITE.url}/og-default.png`,
    email: SITE.email,
    // E.164 — must match the GBP listing and every directory citation byte-for-byte.
    telephone: SITE.phoneE164,
    // The operating warehouse, not the registered office — this is the address
    // that must match Google Business Profile and every directory citation.
    address: {
      '@type': 'PostalAddress',
      streetAddress: LEGAL.warehouse.street,
      postalCode: LEGAL.warehouse.zip,
      addressLocality: LEGAL.warehouse.city,
      addressCountry: 'BE',
    },
    // Must stay byte-identical to the hours on the OSM node, the Google
    // Business Profile and Apple Business Connect — Google cross-checks them,
    // and a mismatch weakens the listing. The human-readable version lives in
    // `contact.responseDesc` in each locale file; change all four together.
    openingHours: 'Mo-Fr 07:00-22:00',
    priceRange: '€€',
    // JSON-LD only — never rendered on the page; feeds LocalBusiness/Maps eligibility.
    geo: {
      '@type': 'GeoCoordinates',
      latitude: LEGAL.warehouse.lat,
      longitude: LEGAL.warehouse.lng,
    },
    areaServed: ['Europe', 'United Kingdom', 'Switzerland'],
    serviceType: ORG_SERVICE_TYPES[locale],
    sameAs: SAME_AS,
  }
}

/**
 * The named author entity, emitted once per article page alongside the
 * `BlogPosting` that references it by `@id`. `jobTitle` and `sameAs` are only
 * emitted when filled — an empty string or array would be a schema smell.
 */
export function authorSchema() {
  return {
    '@type': 'Person',
    '@id': AUTHOR.id,
    name: AUTHOR.name,
    ...(AUTHOR.jobTitle ? { jobTitle: AUTHOR.jobTitle } : {}),
    ...(AUTHOR.sameAs.length ? { sameAs: AUTHOR.sameAs } : {}),
    worksFor: { '@id': `${SITE.url}/#organization` },
  }
}

export function websiteSchema(locale: Locale) {
  return {
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    url: SITE.url,
    name: 'Yaslan',
    inLanguage: locale,
    publisher: { '@id': `${SITE.url}/#organization` },
  }
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${SITE.url}${it.path}`,
    })),
  }
}

/**
 * Trims a meta description to a clean word boundary. Google renders ~155-160
 * chars; slicing at a fixed length mid-word produced snippets like
 * "…pour comprendre la tari", which kill CTR. Collapses whitespace, cuts on the
 * last whole word ≤ max, strips dangling punctuation, and appends an ellipsis.
 */
export function clampMeta(text: string, max = 155): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  const slice = clean.slice(0, max)
  const lastSpace = slice.lastIndexOf(' ')
  const body = lastSpace > 0 ? slice.slice(0, lastSpace) : slice
  return `${body.replace(/[\s.,;:!?…–—-]+$/u, '')}…`
}

/** FAQPage JSON-LD from a list of Q/A pairs (rich results + AI answerability). */
export function faqPageSchema(
  faqs: { q: string; a: string }[],
  opts: { id?: string; inLanguage?: Locale } = {},
) {
  return {
    '@type': 'FAQPage',
    ...(opts.id ? { '@id': opts.id } : {}),
    ...(opts.inLanguage ? { inLanguage: opts.inLanguage } : {}),
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }
}
